import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ProductCard } from "@/components/ProductCard";
import { formatMs } from "@/components/LockBadge";
import { toast } from "sonner";
import { FamilySection } from "@/components/FamilySection";

const TABS = ["Locks", "Orders", "Favorites", "Family", "School"];

export default function Account() {
  const { user, loading, refresh } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("Locks");
  const [locks, setLocks] = useState([]);
  const [orders, setOrders] = useState([]);
  const [favs, setFavs] = useState([]);
  const [schools, setSchools] = useState([]);

  useEffect(() => { if (!loading && !user) navigate("/auth"); }, [loading, user, navigate]);

  useEffect(() => {
    if (!user) return;
    api.myLocks().then(({ data }) => setLocks(data));
    api.orders().then(({ data }) => setOrders(data));
    api.favorites().then(({ data }) => setFavs(data));
    api.schools().then(({ data }) => setSchools(data));
  }, [user]);

  if (!user) return null;

  const join = async (sid) => {
    try { await api.joinSchool(sid); await refresh(); toast.success("School verified!"); }
    catch (e) { toast.error(errMsg(e)); await refresh(); }
  };

  return (
    <div data-testid="account-page" className="max-w-[1200px] mx-auto px-5 sm:px-8 pt-12">
      <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
        <div>
          <p className="font-label text-[10px] text-rosemetal mb-2">Your Atelier</p>
          <h1 className="font-display text-5xl sm:text-6xl">Hi, {user.name.split(" ")[0]}</h1>
          <p className="font-body text-sm text-muted-foreground mt-2">
            {user.school_verified ? "School verified" : "No school linked"}
          </p>
        </div>
        <button
          data-testid="sign-out-all-btn"
          onClick={async () => {
            if (!window.confirm("Sign out of Heather's Boutique on every device? You'll stay signed in here.")) return;
            try {
              const { data } = await api.logoutAll();
              localStorage.setItem("hb_token", data.token);
              toast.success("Signed out on every other device.");
            } catch (e) { toast.error(errMsg(e)); }
          }}
          className="font-label text-[9px] tracking-widest text-ink/50 hover:text-rosemetal border border-border hover:border-rosemetal px-3 py-2 rounded-sm transition-colors"
        >
          SIGN OUT ALL SESSIONS
        </button>
      </div>

      <div className="flex gap-6 border-b border-border mb-10">
        {TABS.map((t) => (
          <button key={t} data-testid={`account-tab-${t.toLowerCase()}`} onClick={() => setTab(t)} className={`font-label text-[10px] pb-4 -mb-px border-b-2 transition-colors ${tab === t ? "border-rosemetal text-ink" : "border-transparent text-muted-foreground"}`}>{t}</button>
        ))}
      </div>

      {tab === "Locks" && (
        <div className="space-y-4" data-testid="locks-panel">
          {locks.length === 0 ? <p className="font-body text-muted-foreground">No locks yet. Lock a dress to hold it for your school.</p> :
            locks.map((l) => (
              <div key={l.id} className="flex items-center justify-between border border-border rounded-sm p-5">
                <div>
                  <Link to={`/product/${l.product_id}`}><h3 className="font-display text-2xl">{l.product_name}</h3></Link>
                  <p className="font-body text-xs text-muted-foreground mt-1 capitalize">{l.status}</p>
                </div>
                <div className="text-right font-body text-sm tabular-nums">
                  {l.status === "active" ? formatMs(Math.max(0, new Date(l.expires_at) - Date.now())) : l.status === "purchased" ? "Secured forever" : "—"}
                </div>
              </div>
            ))}
        </div>
      )}

      {tab === "Orders" && (
        <div className="space-y-4" data-testid="orders-panel">
          {orders.length === 0 ? <p className="font-body text-muted-foreground">No orders yet.</p> :
            orders.map((o) => (
              <div key={o.id} className="border border-border rounded-sm p-5">
                <div className="flex justify-between mb-3"><span className="font-label text-[9px] text-muted-foreground">{new Date(o.created_at).toLocaleDateString()}</span><span className="font-body text-sm">${o.total}</span></div>
                {o.items.map((it) => <p key={it.product_id} className="font-body text-sm">{it.name} · {it.size} × {it.quantity}</p>)}
              </div>
            ))}
        </div>
      )}

      {tab === "Favorites" && (
        <div data-testid="favorites-panel">
          {favs.length === 0 ? <p className="font-body text-muted-foreground">No favorites yet.</p> :
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-5 gap-y-10">{favs.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}</div>}
        </div>
      )}

      {tab === "Family" && (
        <div data-testid="family-panel">
          <FamilySection user={user} />
        </div>
      )}

      {tab === "School" && (
        <div data-testid="school-panel">
          <p className="font-body text-sm text-muted-foreground mb-6 max-w-lg">Link your school to unlock dress locks, squads, and your school portal. Your email domain must match the school.</p>
          <div className="grid md:grid-cols-2 gap-4">
            {schools.map((s) => (
              <div key={s.id} className={`border rounded-sm p-5 flex items-center justify-between ${user.school_id === s.id ? "border-rosemetal bg-blush" : "border-border"}`}>
                <div>
                  <h3 className="font-display text-2xl">{s.name}</h3>
                  <p className="font-body text-xs text-muted-foreground">{s.city}, {s.state} · @{s.domains[0]}</p>
                </div>
                {user.school_id === s.id && user.school_verified ? (
                  <span className="font-label text-[9px] text-rosemetal">Verified</span>
                ) : (
                  <button data-testid={`join-${s.id}`} onClick={() => join(s.id)} className="border border-ink px-4 py-2 font-label text-[9px] rounded-sm">Join</button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
